import { type ComponentType, type FC, useMemo } from "react";
import type {
    CustomerDisplayComponentType,
    CustomerDisplayTypeUnion,
} from "../../Routes/Settings/CustomerDisplay/CustomerDisplayTypes";
import { currency } from "../../Utilities/Generic/Numbers";
import { CustomerDisplayImage } from "./Components/CustomerDisplayImage";
import { CustomerDisplaySaleTable } from "./Components/CustomerDisplaySaleTable";
import { CustomerDisplaySlideShow } from "./Components/CustomerDisplaySlideShow";
import { CustomerDisplayText } from "./Components/CustomerDisplayText";
import { CustomerDisplayTotals } from "./Components/CustomerDisplayTotals";
import type {
    CustomerDisplayComponentProps,
    CustomerDisplayData,
    CustomerDisplayProductData,
    CustomerDisplayTotal,
} from "./CustomerDisplay";

interface Props {
    component: Omit<CustomerDisplayComponentType, "position">;
    data: CustomerDisplayData;
    done: boolean;
}

type CustomerDisplayContainedComponent = ComponentType<CustomerDisplayComponentProps<any, any>>;

const CUSTOMER_DISPLAY_COMPONENTS: Record<CustomerDisplayTypeUnion, CustomerDisplayContainedComponent> = {
    slideshow: CustomerDisplaySlideShow,
    sale     : CustomerDisplaySaleTable,
    totals   : CustomerDisplayTotals,
    image    : CustomerDisplayImage,
    text     : CustomerDisplayText,
};

function prepareTotals(
    data: CustomerDisplayData,
    done: boolean
): CustomerDisplayTotal {
    const results: CustomerDisplayTotal = {
        total    : false,
        savings  : false,
        change   : false,
        approved : false,
        thanks   : false,
        remaining: false,
        paid     : false,
    };

    if(done) {
        if(data.totals.cashout > 0) {
            // Display cashout message (approved, do not display the cashout)
            results.approved = true;
        } else if(data.totals.change > 0) {
            // Display change message
            results.change = currency(data.totals.change);
        } else if(data.eftpos) {
            // Display eftpos approval message
            results.approved = true;
        } else {
            // Display generic message
            results.thanks = true;
        }
    } else {
        results.total = data.totals.total;

        if(data.totals.remaining) {
            results.remaining = currency(data.totals.remaining);
        }

        if(data.totals.paid) {
            results.paid = currency(data.totals.paid);
        }

        if(data.totals.savings) {
            results.savings = currency(data.totals.savings);
        }
    }

    return results;
}

export const CustomerDisplayComponent: FC<Props> = ({
    component,
    data,
    done,
}) => {
    const type = component.type;

    const componentData = useMemo(() => {
        switch(type) {
            case "sale":
                return {
                    products : data.sale,
                    surcharge: data.totals.surcharge,
                } as CustomerDisplayProductData;
            case "totals":
                return prepareTotals(data, done);
            case "text":
            case "image":
            case "slideshow":
            default:
                return null;
        }
    }, [ type, done, data ]);

    const Component = CUSTOMER_DISPLAY_COMPONENTS[type] as ComponentType<CustomerDisplayComponentProps<any, any>>;

    return (
        <Component
            data={componentData}
            settings={component.settings as any}
        />
    );
};
